import { Box, Divider, Paper, Stack, Typography } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import dayjs from "dayjs";
import { useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useParams, useSearchParams } from "react-router-dom";
import { SINGLE_BOX_KEY } from "../../../../../enums/QueryKeys";
import { trimText } from "../../../../../utils/Functions";

export default function InpatientStayOveviewStep({ selectedPetId }) {
  const { t } = useTranslation();
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const formValues = useWatch();
  const queryClient = useQueryClient();
  const { pets } = queryClient.getQueryData([SINGLE_BOX_KEY + id]);
  const petStayId = selectedPetId;

  const {
    dateOfEntrance,
    dateOfExit,
    ampoule,
    fivFelv,
    corporal,
    vaccines,
    antiParasite,
    dateOfAntiParasite,
    nextAntiParasiteDate,
    nextVaccineDate,
  } = formValues;

  const formatDate = (date) => (date ? dayjs(date).format("DD/MM/YYYY") : "-");

  const petToDisplay = petStayId ? getPetById(petStayId) : pets[0];

  function getPetById(id) {
    return pets.filter((pet) => String(pet?.id) === String(id));
  }

  return (
    <Stack spacing={1}>
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          {t("Pet")}
        </Typography>
        <Typography variant="body1">
          {petToDisplay[0]?.name || t("No pet selected")}
        </Typography>
      </Paper>

      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          {t("Details")}
        </Typography>
        <Stack spacing={1}>
          <Typography variant="body2">
            <strong>{t("DateOfEntrance")}:</strong> {formatDate(dateOfEntrance)}
          </Typography>
          <Typography variant="body2">
            <strong>{t("DateOfExit")}:</strong> {formatDate(dateOfExit)}
          </Typography>
          <Typography variant="body2" title={ampoule}>
            <strong>{t("Ampoule")}:</strong>{" "}
            {trimText(ampoule, 100) || t("None")}
          </Typography>
          <Typography variant="body2">
            <strong>{t("FIV/FELV")}:</strong> {fivFelv || t("-")}
          </Typography>
          <Typography variant="body2">
            <strong>{t("Corporal")}:</strong> {corporal ? t("Yes") : t("No")}
          </Typography>
        </Stack>
      </Paper>

      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          {t("Vaccines")}
        </Typography>
        {vaccines && vaccines.length > 0 ? (
          <Stack spacing={2}>
            {vaccines.map((vaccine, index) => (
              <Box key={index}>
                <Stack
                  direction={"row"}
                  justifyContent={"space-between"}
                  alignItems={"center"}
                >
                  <Typography
                    variant="subtitle1"
                    sx={{ wordBreak: "break-all", maxWidth: 700 }}
                  >
                    {vaccine.name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t("Category")}: {t(vaccine.category)} | {t("Date")}:{" "}
                    {formatDate(vaccine.date)}
                  </Typography>
                </Stack>
                {index < vaccines.length - 1 && <Divider sx={{ mt: 2 }} />}
              </Box>
            ))}
          </Stack>
        ) : (
          <Typography variant="body2" color="text.secondary">
            {"N/A"}
          </Typography>
        )}
      </Paper>

      <Paper sx={{ p: 3 }}>
        <Typography mt={3} variant="h6">
          <strong>{t("NextVaccinationDate")}:</strong>{" "}
        </Typography>
        <Typography variant="body2" sx={{ mt: 2 }}>
          {formatDate(nextVaccineDate)}
        </Typography>
      </Paper>
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          {t("AntiParasite")}
        </Typography>
        <Stack spacing={1}>
          <Typography variant="body2">
            <strong>{t("Title")}:</strong> {antiParasite || t("None specified")}
          </Typography>
          <Typography variant="body2">
            <strong>{t("DateOfAntiParasite")}:</strong>{" "}
            {formatDate(dateOfAntiParasite)}
          </Typography>
          <Typography variant="body2">
            <strong>{t("AntiParasiteNextDate")}:</strong>{" "}
            {formatDate(nextAntiParasiteDate)}
          </Typography>
        </Stack>
      </Paper>
    </Stack>
  );
}
